import { useMemo } from "react";
import type { FormEvent } from "react";

import { useDialogForm } from "../../../../hooks";
import { useStableRest } from "../../../../utils/use-stable-rest";
import { useFilterPanelContext } from "../../filter-panel.context";
import { validateSavedFilterName } from "../../filter-panel.model";
import { SAVED_FILTER_NAME_MAX_LENGTH } from "../../messages";
import type { FilterPanelSaveDialogViewProps, SaveDialogProps } from "./types-save-filter-dialog";

/** ViewModel do SaveDialog: nome validado em tela (obrigatório, até 50, único, limite) e envio via `onSave`; sem JSX. */
export function useFilterPanelSaveDialog({
  visible,
  onClose,
  onSave,
  savedFilters,
  className,
  ...props
}: SaveDialogProps): FilterPanelSaveDialogViewProps {
  const rest = useStableRest(props);
  const { valueToSave, partTestId } = useFilterPanelContext();

  const form = useDialogForm<{ name: string }>({
    initial: { name: "" },
    isOpen: visible,
    validate: (values) => {
      const error = validateSavedFilterName(values.name, savedFilters);
      return error ? { name: error } : {};
    },
    onSubmit: (values) => {
      const result = onSave(values.name.trim(), valueToSave());
      if (!result.ok) throw new Error(result.error);
      onClose();
    },
  });
  const { values, errors, submitError, isSubmitting, setValue, submit } = form;
  const testId = partTestId("save");

  return useMemo(
    () => ({
      visible,
      onClose,
      rest,
      className,
      testId,
      dialogTestId: `${testId}-dialog`,
      footerTestId: `${testId}-footer`,
      nameTestId: `${testId}-name`,
      formId: `${testId}-form`,
      name: values.name,
      maxLength: SAVED_FILTER_NAME_MAX_LENGTH,
      error: errors.name ?? submitError,
      locked: isSubmitting,
      onNameChange: (event: { target: { value: string } }) => setValue("name", event.target.value),
      onSubmit: (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        void submit();
      },
    }),
    [
      visible,
      onClose,
      rest,
      className,
      testId,
      values.name,
      errors.name,
      submitError,
      isSubmitting,
      setValue,
      submit,
    ],
  );
}
